import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteMyday } from '../fetchHooks/deleteMyday';
import { useMydays } from '../fetchHooks/useMydays';
import type { MyDayVariant } from '../pages/MyDayPage/MyDayPage';

/**
 * Common hook for Myday table logic
 * Provides pagination, data fetching, and common actions
 */
export const useMydayTable = (variant: MyDayVariant, initialPageSize = 10) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(initialPageSize);

  const { data: mydaysResponse, isLoading: isLoadingMydays } = useMydays(currentPage, pageSize, variant);

  // Handle both new paginated format { data: [], pagination: {} } and old array format []
  const mydays = Array.isArray(mydaysResponse) ? mydaysResponse : mydaysResponse?.data || [];
  const pagination = Array.isArray(mydaysResponse) ? null : mydaysResponse?.pagination;

  const handleShowMydayDetails = ({ id }: { id: number }) => navigate(`/mydaydetails/${id}`);

  const handleDeleteMyday = (id: number) => {
    deleteMyday(
      id,
      () => {
        queryClient.invalidateQueries({ queryKey: ['useMydays'] });
      },
      true,
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tablePagination = pagination
    ? {
        current: pagination.page,
        pageSize: pagination.limit,
        total: pagination.total,
        onChange: handlePageChange,
      }
    : undefined;

  return {
    mydays,
    pagination,
    isLoadingMydays,
    tablePagination,
    handleShowMydayDetails,
    handleDeleteMyday,
    currentPage,
    pageSize,
  };
};
